import Vue from 'vue'
import gql from 'graphql-tag'
import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Export } from '@/modules/DownloadReports/interfaces/export'
import { Carrier } from '@/modules/Carrier/interfaces/carrier'
import { CarrierProduct } from '@/modules/CarrierProduct/interfaces/carrier-product'
import { ApolloResponse } from '@/interfaces/apollo'
import { showErrors, showNotification } from '@/utils/snack'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppArcSelector from '@/components/AppArcSelector.vue'
import { getExports, deleteExport } from '@/modules/DownloadReports/gql/exports.gql'
import { dateToDisplay } from '@/utils/dateTime'
import S3Handler from '@/mixins/s3'
import { policyTypeKeys } from '@/modules/Policy/helpers/policy'
import { SnackType } from '@/interfaces/snack'

export interface InfoItem {
  title: string
  value: string
}

@Component({
  components: {
    AppBreadcrumbs,
    AppDialog,
    AppArcSelector
  },
  beforeRouteEnter (to: any, from: any, next: any) {
    if (to.params.type === 'case_report') {
      to.meta.menu = 'CaseReports'
    } else if (to.params.type === 'report_rules') {
      to.meta.menu = 'ReportsRules'
    } else {
      to.meta.menu = 'PolicyReports'
    }

    next()
  }
})
export default class DownloadReports extends mixins(S3Handler) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Reports'
    }
  ]

  exports: Export[] = []
  tableLoading = false

  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'name',
    sortDesc: true
  }

  tableColumn = [
    { field: 'name',
      title: 'FileName',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.file ? row.file.name : '-')
        ]
      }
      },
      resizable: true
    },
    { field: 'createdAt', title: 'Created Date', slots: { default: 'createat_default' }, width: 130 },
    { field: 'creator',
      title: 'Creator',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.createdBy ? row.createdBy.username : '-')
        ]
      }
      },
      width: 160,
      resizable: true
    },
    { title: 'Filter',
      field: 'filterStr',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.filterVal ? row.filterVal : '-')
        ]
      }
      },
      resizable: true
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 90 }
  ]

  type = ''

  dateToDisplay = dateToDisplay

  deleteExportDialog = {
    show: false,
    title: 'Delete Export',
    text: ''
  }
  targetExport: Export | null = null
  disableConfirm = false

  complexSortItems = ['expired in last 30 days', 'expire in next 30 days', 'commission report']
  complexSortVals = ['EXPIRED_POLICY', 'EXPIRING_POLICY', 'COMMISSION_REPORT']
  complexSortActive = -1

  async mounted () {
    if (this.$route.params.type === 'case_report') {
      const breadcrumbList = [
        {
          text: 'Case Reports',
          to: { name: 'CaseReports' }
        },
        {
          text: 'Download Reports'
        }
      ]
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs = this.breadcrumbs.concat(breadcrumbList)
      }
    } else if (this.$route.params.type === 'report_rules') {
      const breadcrumbList = [
        {
          text: 'Reports',
          to: { name: 'ReportsRules' }
        },
        {
          text: 'Download Reports'
        }
      ]
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs = this.breadcrumbs.concat(breadcrumbList)
      }
    } else {
      const breadcrumbList = [
        {
          text: 'Policy Reports',
          to: { name: 'PolicyReports' }
        },
        {
          text: 'Download Reports'
        }
      ]
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs = this.breadcrumbs.concat(breadcrumbList)
      }
    }

    this.translateType(this.$route.params.type)
    this.onResize()
    this.getExportList()
  }

  translateType (type: string) {
    let typeText = ''
    let typeIdx = -1
    switch (type) {
      case 'expired_policy':
        typeText = 'EXPIRED_POLICY'
        typeIdx = 0
        break
      case 'expiring_policy':
        typeText = 'EXPIRING_POLICY'
        typeIdx = 1
        break
      case 'commission_report':
        typeText = 'COMMISSION_REPORT'
        typeIdx = 2
        break
      case 'binding_log':
        typeText = 'BINDING_LOG'
        break
      case 'case_report':
        typeText = 'CASE_REPORT'
        break
      case 'report_rules':
        typeText = 'CUSTOM_POLICY_REPORT'
        break
      default:
        break
    }
    this.type = typeText
    this.complexSortActive = typeIdx
  }

  onResize () {
    const container = this.$refs.reportscontainer as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  filterDataInit (list: Array<Export>) {
    const exports = JSON.parse(JSON.stringify(list))
    this.$R.forEach((exportItem: Export) => {
      exportItem.filterVal = ''
      const filters = JSON.parse(exportItem.filterStr)
      if (exportItem.type === 'COMMISSION_REPORT') {
        if (filters['carrierProductName']) {
          exportItem.filterVal += `Carrier Product: ${filters['carrierProductName']}; `
        }
        if (filters['carrierName']) {
          exportItem.filterVal += `Carrier: ${filters['carrierName']}; `
        }
        if (filters['sourceName']) {
          exportItem.filterVal += `Source: ${filters['sourceName']}; `
        }
        if (filters['recordTypeName']) {
          exportItem.filterVal += `Record Type: ${filters['recordTypeName']}; `
        }
        if (filters['gaName']) {
          exportItem.filterVal += `General Agent: ${filters['gaName']}; `
        }
        if (filters['bookName']) {
          exportItem.filterVal += `Book: ${filters['bookName']}; `
        }
        if (filters['type']) {
          exportItem.filterVal += `Policy Type: ${policyTypeKeys[filters['type']]}; `
        }
        if (filters['type']) {
          exportItem.filterVal += `Policy Type: ${policyTypeKeys[filters['type']]}; `
        }
        if (filters['effectiveStartDate'] || filters['effectiveEndDate']) {
          exportItem.filterVal += `Effective Date: ${filters['effectiveStartDate'] ? dateToDisplay(+filters['effectiveStartDate']) : ''}${filters['effectiveStartDate'] && filters['effectiveEndDate'] ? ' - ' : ''}${filters['effectiveEndDate'] ? dateToDisplay(+filters['effectiveEndDate']) : ''}; `
        }
        if (filters['expirationStartDate'] || filters['expirationEndDate']) {
          exportItem.filterVal += `Expiration Date: ${filters['expirationStartDate'] ? dateToDisplay(+filters['expirationStartDate']) : ''}${filters['expirationStartDate'] && filters['expirationEndDate'] ? ' - ' : ''}${filters['expirationEndDate'] ? dateToDisplay(+filters['expirationEndDate']) : ''}; `
        }
        if (filters['bindStartDate'] || filters['bindEndDate']) {
          exportItem.filterVal += `Bind Date: ${filters['bindStartDate']
            ? dateToDisplay(+filters['bindStartDate']) : ''}${filters['bindStartDate'] && filters['bindEndDate'] ? ' - ' : ''}${filters['bindEndDate'] ? dateToDisplay(+filters['bindEndDate']) : ''}; `
        }
        if (filters['cancelStartDate'] || filters['cancelEndDate']) {
          exportItem.filterVal += `Cancel Date: ${filters['cancelStartDate'] ? dateToDisplay(+filters['cancelStartDate']) : ''}${filters['cancelStartDate'] && filters['cancelEndDate'] ? ' - ' : ''}${filters['cancelEndDate'] ? dateToDisplay(+filters['cancelEndDate']) : ''}; `
        }
      }
      if (exportItem.type === 'EXPIRED_POLICY') {
        exportItem.filterVal += 'expired in last 30 days'
      }
      if (exportItem.type === 'EXPIRING_POLICY') {
        exportItem.filterVal += 'expire in next 30 days'
      }

      if (exportItem.type === 'CUSTOM_POLICY_REPORT') {
        exportItem.filterVal = filters.ruleName
      }
    }, exports)
    return exports
  }

  changeComplexSortActive () {
    this.type = this.complexSortVals[this.complexSortActive]
    this.getExportList()
  }

  async getExportList () {
    const { currentPage, pageSize } = this.tableOptions.tablePage
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getExports,
        variables: {
          type: this.type,
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      result.data.getExports.exports = this.$R.sortWith([this.$R.descend(this.$R.prop('createdAt'))])(result.data.getExports.exports)
      this.exports = this.filterDataInit(result.data.getExports.exports)
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = result.data.getExports.paging.totalCount
      this.tableLoading = false
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  handlePageChange (pageData: any) {
    this.tableOptions.tablePage.currentPage = pageData.currentPage
    this.tableOptions.tablePage.pageSize = pageData.pageSize
    this.getExportList()
  }

  async downloadFileHandler (file: any) {
    this.tableLoading = true
    await this.downloadFile(file)
    this.tableLoading = false
  }

  deleteExportHandler (item: Export) {
    this.targetExport = item
    this.deleteExportDialog.show = true
  }

  async deleteExportConfirm () {
    if (!this.targetExport) {
      return
    }
    const { id } = this.targetExport
    this.deleteExportDialog.show = false
    try {
      const result = await this.$apollo.mutate({
        mutation: deleteExport,
        variables: {
          exportId: Number(id)
        }
      })

      const res = result.data.deleteExport
      if (res) {
        showNotification({
          msg: 'Delete insurance export successfully',
          type: SnackType.Success
        })
      }
      this.targetExport = null
      this.getExportList()
    } catch (error) {
      showErrors(error.graphQLErrors)
    }
  }

  routeToDetail (item: any) {
    return {
      name: 'PolicyDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  routeToCarrierDetail (item: any) {
    return {
      name: 'CarrierDetailDetail',
      params: {
        id: item.carrier.id
      }
    }
  }

  routeToCarrierProductDetail (item: any) {
    return {
      name: 'CarrierProductDetailDetail',
      params: {
        id: item.carrierProduct.id
      }
    }
  }
}
